import * as React from 'react';
import { VisibilityCard } from './visibility/VisibilityCard';
import { SectionHeader } from './shared/SectionHeader';
import { StripeGateModal } from '@/features/account/components/StripeGateModal';
import { useStripePublicGate } from '@/features/account/hooks/useStripePublicGate';
import globeIcon from '@/assets/images/svg/globe_icon.svg';
import privateIcon from '@/assets/images/svg/private_icon.svg';

interface VisibilityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (isPublic: boolean) => void;
  isSaving: boolean;
}

export const VisibilityModal: React.FC<VisibilityModalProps> = ({
  isOpen,
  onClose,
  onSave,
  isSaving,
}) => {
  const [isPublic, setIsPublic] = React.useState(false);
  const selectPublic = React.useCallback(() => setIsPublic(true), []);
  const {
    trigger: handlePublicClick,
    isWarningOpen,
    closeWarning,
  } = useStripePublicGate(selectPublic);

  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50'>
      <div className='mx-4 w-full max-w-[700px] rounded-20 bg-white p-6 shadow-xl md:p-10'>
        <div className='flex flex-col gap-2'>
          <SectionHeader title='Choose Echo Visibility' />
          <p className='font-sans text-16 font-normal leading-24 tracking-normal text-gray-600'>
            Select how you want your digital echo to be accessible to others.
          </p>
        </div>

        <div className='mt-6 flex flex-col gap-4'>
          <VisibilityCard
            title='Public Echo'
            subtitle='All your fans can see your digital echo, they can find it, chat with it, and pay to subscribe for exclusive conversations with your echo.'
            icon={<img src={globeIcon} alt='Public' className='size-full object-contain' />}
            isSelected={isPublic}
            onClick={handlePublicClick}
            disabled={isSaving}
          />

          <VisibilityCard
            title='Private Echo'
            subtitle='Your echo stays private, only you can see it.'
            icon={<img src={privateIcon} alt='Private' className='size-full object-contain' />}
            isSelected={!isPublic}
            onClick={() => setIsPublic(false)}
            disabled={isSaving}
          />
        </div>

        <div className='mt-8 flex gap-3'>
          <button
            onClick={onClose}
            disabled={isSaving}
            className='flex-1 rounded-10 border border-gray-300 px-4 py-2.5 font-sans text-14 font-semibold text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50'
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(isPublic)}
            disabled={isSaving}
            className='flex-1 rounded-10 bg-brand-800 px-4 py-2.5 font-sans text-14 font-bold text-white transition-colors hover:bg-brand-700 disabled:opacity-50'
          >
            {isSaving ? 'Saving...' : 'Save Echo'}
          </button>
        </div>
      </div>

      <StripeGateModal isOpen={isWarningOpen} onClose={closeWarning} onProceed={selectPublic} />
    </div>
  );
};
